import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { FocusEvent, KeyboardEvent } from 'react';

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** Commits the edit. Runs on blur, which Escape and Cmd/Ctrl+Enter trigger. */
  onBlur?: () => void;
  placeholder?: string;
  'aria-label': string;
  /** Visible lines at rest and while editing. The box is exactly this tall. */
  lines?: number;
};

/**
 * Multi-line prose edited where it sits: a media row's description. The
 * multi-line sibling of `InlineRenameField` (docs/design-system.md §
 * "Inline multi-line editing").
 *
 * - Resting: no border, no fill. Hover: the `--sidebar-hover` wash.
 * - Open: `--input-background` fill plus the neutral focus border and ring.
 * - Identical padding and 1px border in both states, so the text never moves.
 * - The box is a whole number of lines tall, padding outside the scroll area,
 *   so no line is ever cut through its middle. Longer text scrolls while open
 *   and goes back to its first lines on blur.
 *
 * Replaces `AutoResizeableMultilineInput` on the Media panel rows, whose 54px
 * box showed three and a third 16px lines and whose focus ring sat on a
 * borderless box (PLAT-12136).
 */
const InlineDescriptionField = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Add a description',
  lines = 3,
  'aria-label': ariaLabel,
}: Props) => {
  const [draft, setDraft] = useState(value);
  const isEditingRef = useRef(false);

  // Follow the record when it changes underneath (a regenerated description),
  // but never while the person is typing.
  useEffect(() => {
    if (!isEditingRef.current) setDraft(value);
  }, [value]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' || (e.key === 'Enter' && (e.metaKey || e.ctrlKey))) {
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.blur();
    }
  };

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    isEditingRef.current = false;
    e.currentTarget.scrollTop = 0;
    onBlur?.();
  };

  return (
    <Box onClick={e => e.stopPropagation()}>
      <TextArea
        aria-label={ariaLabel}
        $lines={lines}
        value={draft}
        placeholder={placeholder}
        onFocus={() => {
          isEditingRef.current = true;
        }}
        onChange={e => {
          setDraft(e.target.value);
          onChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    </Box>
  );
};

export default InlineDescriptionField;

const Box = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  min-width: 0;
  padding: 3px 5px;
  border: 1px solid transparent;
  border-radius: var(--radius-input);
  cursor: text;
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast);

  &:hover {
    background-color: var(--sidebar-hover);
  }

  &:focus-within {
    background-color: var(--input-background);
    border-color: var(--focus-border);
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const TextArea = styled.textarea<{ $lines: number }>`
  box-sizing: content-box;
  flex: 1;
  min-width: 0;
  height: calc(var(--leading-text-xs) * ${props => props.$lines});
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  resize: none;
  overflow: hidden;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-xs);
  color: var(--text-secondary);

  &:focus {
    overflow-y: auto;
    color: var(--foreground);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;
